import React, { useContext, useEffect, useState } from "react";
import { Alert as Alert2, Button, ButtonGroup, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row, Table } from "reactstrap";
import _ from "lodash";
import { CartProductCard } from ".";
import { api, helpers, constants, UserContext } from "../utils";
import { AiOutlineCloseSquare } from "react-icons/ai";
import { toast } from "react-toastify";

const { validateLineItemQty, alertMinQty } = helpers;
// Shown when a refused cart-line save comes back without a message of its own.
const CART_LINE_SAVE_REFUSED = "This cart line could not be saved.";

export default function CartCurrent(props) {
   const userCtx = useContext(UserContext);
   const modalClassName = userCtx.theme ? `${userCtx.theme}` : "lightTheme";
   const { currentUser } = userCtx;
   const [confirmClear, setConfirm] = useState(false);
   const [errors, setErrors] = useState([]);
   const [isEditing, setIsEditing] = useState(false);
   const [shoppingCart, setShoppingCart] = useState({ cartItems: [] });
   const [siteItems, setSiteItems] = useState({});
   const [subtotal, setSubtotal] = useState(0.0);

   useEffect(() => {
      if (userCtx && userCtx.shoppingCart) {
         setErrors([]);
         setSubtotal(
            _.reduce(
               userCtx.shoppingCart.cartItems,
               (sum, item) => { return sum + item?.extendedPrice; },
               0
            )
         );

         const newSites = {};
         _.forEach(userCtx.shoppingCart.cartItems, (item) => {
            if (item.itemNumber.startsWith("SP0")) {
               if (newSites[`${item.orderSite}-Sample`]) {
                  newSites[`${item.orderSite}-Sample`].items.push(item);
               } else {
                  newSites[`${item.orderSite}-Sample`] = {
                     items: [item],
                     showPriceOverride: false,
                     approvePriceOverride: false,
                  };
               }
            } else {
               if (newSites[item.orderSite]) {
                  newSites[item.orderSite].items.push(item);
               } else {
                  newSites[item.orderSite] = {
                     items: [item],
                     showPriceOverride: false,
                     approvePriceOverride: false,
                  };
               }
            }
         });
         setSiteItems(newSites);
         setShoppingCart(userCtx.shoppingCart);
      }
   }, [userCtx, shoppingCart]);

   function clearCart() {
      var cartCopy = _.assign({}, shoppingCart);
      api
         .post(`Cart/ClearCart/${cartCopy.shoppingCartId}/${cartCopy.customerId}`)
         .then((response) => {
            if (response.data.success) {
               userCtx.setShoppingCart(response.data.emptyCart);
               props.closeCartSidebar();
            } else {
               toast.error(response.data.message);
            }
         })
         .catch(api.catchHandler);
   }

   function deleteCartItem(selectedItem) {
      var cartCopy = _.assign({}, shoppingCart);
      const itemToDelete = _.find(cartCopy.cartItems, item => {
         return item.shoppingCartItemId === selectedItem.shoppingCartItemId
      });

      api
         .post(`Cart/DeleteItemFromCart`, itemToDelete)
         .then((response) => {
            if (response.data.success) {
               cartCopy.cartItems = _.without(cartCopy.cartItems, itemToDelete)
               userCtx.setShoppingCart(cartCopy);
            }
         })
         .catch(api.catchHandler);
   }

   function handlePriceOverride(siteName) {
      const sitesCopy = _.assign({}, siteItems);
      sitesCopy[siteName].approvePriceOverride = !sitesCopy[siteName].approvePriceOverride;
      setSiteItems(sitesCopy);
   }

   function updateCartItem(item) {
      if (currentUser
         && (currentUser.isAPUCS || currentUser.isSalesperson)
         && validateLineItemQty(item.quantity, item.realMinOrderQty, item.baseUnit)
      ) {
         api
            .post(`Cart/SaveShoppingCartItem`, item)
            .then((response) => {
               // A refused save comes back as { success: false, message } - show it, never store it as the line.
               if (response.data?.success === false) {
                  toast.error(_.isString(response.data.message) && response.data.message ? response.data.message : CART_LINE_SAVE_REFUSED);
                  return;
               }
               if (response.data) {
                  var cartCopy = _.assign({}, shoppingCart);
                  const updateIndex = _.findIndex(cartCopy.cartItems, (cartItem) => {
                     return cartItem.itemNumber === item.itemNumber;
                  });
                  cartCopy.cartItems[updateIndex] = response.data;
                  userCtx.setShoppingCart(cartCopy);
               }
            })
            .catch(api.catchHandler)
            .finally(() => setIsEditing(false));
      } else {
         if (!currentUser?.isAPUCS && !currentUser?.isSalesperson) {
            toast.error("User does not have permission to change this cart.");
         } else {
            alertMinQty(item.itemNumber, item.realMinOrderQty, item.baseUnit);
         }
      }
   }

   function validateCart() {
      const errorList = [];
      _.forEach(siteItems, (site, siteName) => {
         const siteSubtotal = _.reduce(
            site.items,
            (sum, item) => { return sum + item.extendedPrice; },
            0
         );

         if (
            siteSubtotal < 50 &&
            !site.approvePriceOverride &&
            !siteName.includes("-Sample")
         ) {
            errorList.push(
               `Order from ${siteName} does not meet minimum price of $50.00. Please remove these items or continue adding product to proceed.`
            );
            if (userCtx.currentUser && (userCtx.currentUser.isAPUCS || userCtx.currentUser.isSalesperson)) {
               const siteCopy = _.assign({}, site);
               siteCopy.showPriceOverride = true;
               const sitesCopy = _.assign({}, siteItems);
               sitesCopy[siteName] = siteCopy;
               setSiteItems(sitesCopy);
            }
         }

         if (siteName === "PLT") {
            _.forEach(site.items, (item) => {
               if (_.startsWith(item.itemClass, "MACH")) {
                  errorList.push(
                     `Machine Parts can NOT come from Site PLT. Please reassign or remove item ${item.itemNumber} to proceed`
                  );
               }
            });
         }

         if (siteName === "FLEX05") {
            _.forEach(site.items, (item) => {
               if (_.startsWith(item.itemNumber, "SP0")) {
                  errorList.push(
                     `Sample Items can NOT come from Site ${siteName}. Please reassign or remove item ${item.itemNumber} to proceed`
                  );
               }
               else if (_.startsWith(item.itemClass, "MACH")) {
                  errorList.push(
                     `Machine Parts can NOT come from Site ${siteName}. Please reassign or remove item ${item.itemNumber} to proceed`
                  );
               }
            });
         }

         if (!_.find(constants.VALID_ORDER_SITES, (validSite) => {
               return validSite === _.trim(siteName, "-Sample");
            })
         ) {
            errorList.push(
               `Currently, the only acceptable order sites are PLT, PLTWHSE, or FLEX05. Please reassign or remove the items from ${siteName} to proceed`
            );
         }
      });

      if (errorList.length) {
         setErrors(errorList);
      } else {
         props.checkoutCallback();
      }
   }

   if (!userCtx || !userCtx.currentUser) return null;
   return (
      <div>
         <div className="cartMenu container fluid pe-5">
            <Row style={{ position: "relative", width: "100%" }}>
               <Col xs="11">
                  <h2 className="text-center">Cart</h2>
               </Col>
               <Col xs="1">
                  <h2 onClick={() => props.closeCartSidebar()} className="float-end">
                     <AiOutlineCloseSquare className="cart-menu-button align-self-center cursor-pointer" />
                  </h2>
               </Col>
            </Row>
            <Row style={{ position: "relative", width: "100%" }}>
               <Col xs="6" className="cartMenuLeftHeader">
                  <h5>
                     {shoppingCart && shoppingCart.cartItems
                        ? shoppingCart.cartItems.length
                        : "0"}{" "}
                     Item(s) in Cart
                  </h5>
                  <span className="ms-3 text-muted pb-2">
                     Customer:{" "}
                     {shoppingCart.customerId
                        ? shoppingCart.customerId
                        : "No Customer Selected"}
                  </span>
               </Col>
            </Row>
            {errors.length
               ? _.map(errors, (error, index) => {
                  return (
                     <Row key={index} className="">
                        <Alert2 color="danger" children={error} />
                     </Row>
                  );
               })
               : null}
            {shoppingCart &&
               shoppingCart.cartItems &&
               shoppingCart.cartItems.length ? (
               _.map(siteItems, (site, siteName) => {
                  return (
                     <CartProductCard
                        key={siteName}
                        siteName={siteName}
                        cartItems={site.items}
                        updateCallback={updateCartItem}
                        deleteCallback={deleteCartItem}
                        showPriceOverride={site.showPriceOverride}
                        approvePriceOverride={site.approvePriceOverride}
                        priceOverrideCallback={handlePriceOverride}
                        isEditing={isEditing}
                        setIsEditing={setIsEditing}
                        allowInlineEditsDeletes={true}
                        subTotal={subtotal}
                     />
                  );
               })
            ) : (
               <div className="h5 text-center">Your shopping cart is empty</div>
            )}
            {props.checkoutCallback && userCtx.currentUser && (userCtx.currentUser.isAPUCS || userCtx.currentUser.isSalesperson || userCtx.currentUser.isQuoteRole || userCtx.currentUser.isAnyCustomerTypeRole) && (
               <Row style={{ position: "relative", width: "100%" }}>
                  <Col>
                     <Button
                        className="shredButton me-2"
                        children="Proceed To Checkout"
                        onClick={validateCart}
                        disabled={
                           !(
                              shoppingCart &&
                              shoppingCart.cartItems &&
                              shoppingCart.cartItems.length
                           )
                        }
                     />
                  </Col>
                  {userCtx.currentUser.isQuoteRole && (
                     <Col>
                        <Button
                           className="shredButton me-2 buttonRight"
                           children="Clear Cart"
                           onClick={() => { setConfirm(true); }}
                           disabled={
                              !(
                                 shoppingCart &&
                                 shoppingCart.cartItems &&
                                 shoppingCart.cartItems.length
                              )
                           }
                        />
                     </Col>
                  )}
               </Row>
            )}
            {props.showActiveCarts && userCtx.activeCarts.length && userCtx.currentUser && !userCtx.currentUser.isAnyCustomerTypeRole ? (
               <>
                  <hr />
                  <Row>
                     <Col>
                        <span className="h5">Other Active Carts</span>
                     </Col>
                  </Row>
                  <Row style={{ position: "relative", width: "100%" }}>
                     <Col>
                        <Table className="data-table" size="sm">
                           <thead className="table-header">
                              <tr>
                                 <th>Customer Number</th>
                                 <th>Number Of Items</th>
                              </tr>
                           </thead>
                           <tbody>
                              {_.chain(userCtx.activeCarts)
                                 .filter(
                                    (cart) =>
                                       cart.customerId !==
                                       (userCtx.customerFilter && userCtx.customerFilter.value)
                                 )
                                 .map((cart, index) => {
                                    return (
                                       <tr
                                          className={`cartItem tr-${index % 2 === 0 ? "primary" : "secondary"}`}
                                          key={cart.customerId}
                                       >
                                          <td className="ps-3">{cart.customerId}</td>
                                          <td>{cart.cartItems.length} Items in Cart</td>
                                       </tr>
                                    );
                                 })
                                 .value()}
                           </tbody>
                           <tfoot className="table-footer">
                              <tr>
                                 <td />
                                 <td />
                              </tr>
                           </tfoot>
                        </Table>
                     </Col>
                  </Row>
               </>
            ) : null}
         </div>

         <Modal style={{ zIndex: 100000 }} id="confirm-delete" isOpen={confirmClear} contentClassName={modalClassName}>
            <ModalHeader children="Please Confirm" />
            <ModalBody>Are you sure? This will remove all items in the cart.</ModalBody>
            <ModalFooter>
               <ButtonGroup>
                  <Button
                     children="Yes"
                     className="button-success"
                     onClick={() => clearCart()}
                  />
                  <Button
                     children="No"
                     className="button-danger"
                     onClick={() => setConfirm(false)}
                  />
               </ButtonGroup>
            </ModalFooter>
         </Modal>
      </div>
   );
}
